/**
 * Tag groups: the part of a tag before the first "/" ("area" in `area/waikiki`) groups tags into
 * a dimension, like a field with values. Filters list tags by group, a tab can group its cards by
 * one ("Group by Area"), and a card's subtitle can show one ("Waikiki" under each item).
 */

/** The group a tag belongs to (`area/waikiki` → `area`), or null for a plain tag. */
export function tagGroupOf(tag: string): string | null {
  const i = tag.indexOf('/')
  return i > 0 ? tag.slice(0, i) : null
}

/** A tag (or part of one) as words: `north-shore` → "North Shore", `type/food` → "Type / Food". */
export function tagLabel(part: string): string {
  return part
    .split('/')
    .map((seg) =>
      seg
        .split(/[-_]+/)
        .filter(Boolean)
        .map((w) => w[0]!.toUpperCase() + w.slice(1))
        .join(' '),
    )
    .join(' / ')
}

/** An item's values in a group, as words: tags `area/waikiki`, `area/north-shore` → ["Waikiki", "North Shore"]. */
export function tagGroupValues(tags: readonly string[], group: string): string[] {
  return tags.filter((t) => t.startsWith(group + '/')).map((t) => tagLabel(t.slice(group.length + 1)))
}

export interface TagGroup {
  /** The part before "/" (what a filter selects to match any of them). */
  group: string
  label: string
  /** How many times its tags are used, all together. */
  count: number
  /** Its tags with how often each is used, most used first. */
  tags: Array<[string, number]>
}

/** Tags with their counts, sorted into groups (most used first) and the plain ones left over. */
export function groupTags(counted: ReadonlyArray<readonly [string, number]>): { groups: TagGroup[]; plain: Array<[string, number]> } {
  const groups = new Map<string, TagGroup>()
  const plain: Array<[string, number]> = []
  for (const [tag, n] of counted) {
    const g = tagGroupOf(tag)
    if (!g) {
      plain.push([tag, n])
      continue
    }
    let entry = groups.get(g)
    if (!entry) groups.set(g, (entry = { group: g, label: tagLabel(g), count: 0, tags: [] }))
    entry.count += n
    entry.tags.push([tag, n])
  }
  const byCount = (a: readonly [string, number], b: readonly [string, number]) => b[1] - a[1] || a[0].localeCompare(b[0])
  for (const g of groups.values()) g.tags.sort(byCount)
  // A tag named like a group ("area" next to "area/waikiki") belongs with it.
  for (const [i, [tag, n]] of [...plain.entries()].reverse()) {
    const g = groups.get(tag)
    if (g) {
      g.count += n
      g.tags.unshift([tag, n])
      plain.splice(i, 1)
    }
  }
  return { groups: [...groups.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)), plain: plain.sort(byCount) }
}

/** Whether `tag` is `path` or sits under it (`area/waikiki` is under `area`). */
export function isUnderTag(tag: string, path: string): boolean {
  return tag === path || tag.startsWith(path + '/')
}

/**
 * One level of the tag tree: the next part after `path` ("" = the first level) in the tags given,
 * with how many uses lead there, most first. `area` over `area/waikiki` (5), `area/honolulu/kaimuki`
 * (2) → [["waikiki", 5], ["honolulu", 2]].
 */
export function tagLevel(counted: ReadonlyArray<readonly [string, number]>, path: string): Array<[string, number]> {
  const out = new Map<string, number>()
  for (const [tag, n] of counted) {
    if (path && !tag.startsWith(path + '/')) continue
    const part = (path ? tag.slice(path.length + 1) : tag).split('/')[0]
    if (part) out.set(part, (out.get(part) ?? 0) + n)
  }
  return [...out.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
}
